import React from "react";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Profile from "./pages/Profile";
import TestimonialDetail from "./pages/TestimonialDetail";
import NotFound from "./pages/NotFound";
import ComingSoon from "./pages/ComingSoon";
import {
  Classes,
  Coaching,
  CourseDetail,
  Dashboard,
  Foundation,
  Members,
  VideoDetail,
} from "./pages/membership";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminVideos from "./pages/admin/AdminVideos";
import AdminBookings from "./pages/admin/AdminBookings";
// import AdminContent from "./pages/admin/AdminContent";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminCourses from "./pages/admin/AdminCourses";
import CourseEditor from "./pages/admin/CourseEditor";
import AdminSurveySubmissions from "./pages/admin/AdminSurveySubmissions";
import AdminBanners from "./pages/admin/AdminBanners";
import AdminSubscriptions from "./pages/admin/AdminSubscriptions";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminZoomMeetings from "./pages/admin/AdminZoomMeetings";
import AdminContactMessages from "./pages/admin/AdminContactMessages";
import AdminEmailBlast from "./pages/admin/AdminEmailBlast";
import AdminMarketingLinks      from "./pages/admin/AdminMarketingLinks";
import AdminMarketingEngagement from "./pages/admin/AdminMarketingEngagement";
import AdminMarketingAnalytics  from "./pages/admin/AdminMarketingAnalytics";
import AdminMarketingWhatsApp   from "./pages/admin/AdminMarketingWhatsApp";
import AdminMarketingReferrals  from "./pages/admin/AdminMarketingReferrals";
import AdminMarketingCoupons    from "./pages/admin/AdminMarketingCoupons";
import PublicQA from "./pages/PublicQA";
import PublicQuiz from "./pages/PublicQuiz";
import PublicTip from "./pages/PublicTip";
import PublicPoll from "./pages/PublicPoll";
import PublicCase from "./pages/PublicCase";
import PublicCountdown from "./pages/PublicCountdown";
import PublicLeaderboard from "./pages/PublicLeaderboard";
import PaymentRequest from "./pages/PaymentRequest";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import SubscriptionFailure from "./pages/SubscriptionFailure";
import SubscribePlan from "./pages/SubscribePlan";
import ChooseLeaflets from "./pages/ChooseLeaflets";
import { Layout } from "./layout";
import MembershipLayout from "./components/membership/MembershipLayout";
import AdminLayout from "./components/admin/AdminLayout";
import AdminRoute from "./components/admin/AdminRoute";
import ProtectedRoute from "./components/ProtectedRoute";
import "./App.css";
import { Route, Routes, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { FavoritesProvider } from "./contexts/FavoritesContext";
import { trackPageView, initTrackingConfig } from "./analytics";
import { useMaintenanceBypass } from "./hooks/useMaintenanceBypass";

const MAINTENANCE_MODE = process.env.REACT_APP_MAINTENANCE_MODE === "true";

function RouteTracker() {
  const { pathname } = useLocation();
  useEffect(() => { trackPageView(pathname); }, [pathname]);
  return null;
}

const App: React.FC = () => {
  useEffect(() => { initTrackingConfig(); }, []);
  const isBypassed = useMaintenanceBypass();

  if (MAINTENANCE_MODE && !isBypassed) {
    return <ComingSoon />;
  }

  return (
    <FavoritesProvider>
      <RouteTracker />
      <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="testimonial/:id" element={<TestimonialDetail />} />
        <Route path="choose-leaflets/:planId" element={<ChooseLeaflets />} />
        <Route
          path="profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Membership Area — requires authentication */}
      <Route
        path="/membership"
        element={
          <ProtectedRoute>
            <MembershipLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="foundation" element={<Foundation />} />
        <Route path="coaching" element={<Coaching />} />
        <Route path="classes" element={<Classes />} />
        <Route path="classes/:id" element={<CourseDetail />} />
        <Route path="video/:id" element={<VideoDetail />} />
        <Route path="members" element={<Members />} />
      </Route>

      {/* Admin Area — separate layout, admin-only */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="videos" element={<AdminVideos />} />
        <Route path="bookings" element={<AdminBookings />} />
        {/* <Route path="content" element={<AdminContent />} /> */}
        <Route path="courses" element={<AdminCourses />} />
        <Route path="courses/new" element={<CourseEditor />} />
        <Route path="courses/:id" element={<CourseEditor />} />
        <Route path="surveys" element={<AdminSurveySubmissions />} />
        <Route path="banners" element={<AdminBanners />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="subscriptions" element={<AdminSubscriptions />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="zoom-meetings" element={<AdminZoomMeetings />} />
        <Route path="contact" element={<AdminContactMessages />} />
        <Route path="email-blast" element={<AdminEmailBlast />} />
        <Route path="marketing"            element={<AdminMarketingLinks />} />
        <Route path="marketing/engagement" element={<AdminMarketingEngagement />} />
        <Route path="marketing/whatsapp"   element={<AdminMarketingWhatsApp />} />
        <Route path="marketing/referrals"  element={<AdminMarketingReferrals />} />
        <Route path="marketing/coupons"    element={<AdminMarketingCoupons />} />
        <Route path="marketing/analytics"  element={<AdminMarketingAnalytics />} />
      </Route>

      <Route path="/qa" element={<PublicQA />} />
      <Route path="/quiz" element={<PublicQuiz />} />
      <Route path="/tip" element={<PublicTip />} />
      <Route path="/poll" element={<PublicPoll />} />
      <Route path="/case" element={<PublicCase />} />
      <Route path="/countdown" element={<PublicCountdown />} />
      <Route path="/leaderboard" element={<PublicLeaderboard />} />

      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/payment" element={<PaymentRequest />} />
      <Route path="/subscription/success" element={<SubscriptionSuccess />} />
      <Route path="/subscription/failure" element={<SubscriptionFailure />} />
      <Route path="/subscribe-plan" element={<SubscribePlan />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/terms" element={<TermsAndConditions />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />

      <Route path="*" element={<NotFound />} />
    </Routes>
    </FavoritesProvider>
  );
};

export default App;
